import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { FederatedSeller, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { useIsHighlightsSectionVisible } from '../../hooks/useIsHighlightsSectionVisible';
import { useOrders } from '../../hooks/useOrders';
import { useSellerRole } from '../../hooks/useSellerRole';
import { OrderPrice } from './OrderPrice';
import { Recommendations } from './Recommendations';
import { Reviews } from './Reviews';
import { TotalOrders } from './TotalOrders';
import { YearsOnFiverr } from './YearsOnFiverr';

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the RolePageHighlights Component');
    logger.error(err, {
        cause: originalError,
    });
};

interface RolePageHighlightsProps {
    className?: string;
    sellerRoleId?: string;
}

export const RolePageHighlights: React.FC<RolePageHighlightsProps> = ({ className, sellerRoleId }) => {
    const isHighlightsSectionVisible = useIsHighlightsSectionVisible();
    const { rating, testimonials } = useSellerData(selectSellerSlice);
    const sellerRole = useSellerRole(sellerRoleId);
    const { totalCount } = useOrders();

    if (!isHighlightsSectionVisible) {
        return null;
    }

    const isRatingPresent = sellerRole ? sellerRole.rating?.count : rating?.count;
    const isRecommendationsPresent = testimonials.length > 0;
    const isOrdersPresent = sellerRole ? sellerRole.completedOrdersCount : totalCount;
    const isOrderPricePresent = sellerRole && sellerRole.startingPrice;

    return (
        <ErrorBoundary onError={onRenderError}>
            <Stack withDivider gap="8" overflowX="auto" className={className}>
                {isRatingPresent ? <Reviews sellerRoleId={sellerRoleId} /> : null}
                {!isRatingPresent && isRecommendationsPresent ? <Recommendations sellerRoleId={sellerRoleId} /> : null}
                {isOrdersPresent ? <TotalOrders sellerRoleId={sellerRoleId} /> : null}
                <YearsOnFiverr sellerRoleId={sellerRoleId} />
                {isOrderPricePresent ? <OrderPrice sellerRoleId={sellerRoleId} /> : null}
            </Stack>
        </ErrorBoundary>
    );
};

const selectSellerSlice = (seller: FederatedSeller) => ({
    rating: seller.rating,
    testimonials: seller.testimonials,
});
